<script setup lang="ts" generic="T extends SelectableValue">
import type { Ref } from 'vue';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport
} from 'radix-vue';
import { vIntersectionObserver } from '@vueuse/components';

import { BadgeSize } from '@/hooks/useBadgeClasses';
import { ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useZindex } from '@/hooks/useZindex';

import {
  MultiSelectBoxProps,
  Selectable,
  SelectableValue,
  useSelectBox
} from '@/components/selectBox/selectBox';

import Badge from '@/components/badge/Badge.vue';
import Checkbox from './Checkbox.vue';
import ComboboxSearchInput from './ComboboxSearchInput.vue';
import IconButton from '@/components/button/IconButton.vue';
import NoOptions from '@/components/selectBox/combobox/NoOptions.vue';
import SelectBoxItem from '@/components/selectBox/SelectBoxItem.vue';
import SelectBoxViewport from '@/components/selectBox/SelectBoxViewport.vue';
import MobileNoResults from './MobileNoResults.vue';

import AngleDownIcon from '@/icons/line/angle-down.svg';
import ArrowLeftIcon from '@/icons/line/arrow-left.svg';
import CheckIcon from '@/icons/line/check.svg';
import DirectionIcon from '@/icons/line/direction.svg';
import LockIcon from '@/icons/line/lock.svg';
import SortingIcon from '@/icons/line/sorting.svg';

const props = withDefaults(defineProps<MultiSelectBoxProps<T>>(), {
  placeholder: 'Choose...',
  selectAll: false,
  selectAllLabel: 'Select all',
  searchable: true
});

const emit = defineEmits<{
  'update:modelValue': [Selectable<T>[]];
  open: [];
}>();

defineSlots<{
  option?: (props: { option: Selectable<T>; checked?: boolean; close: () => void }) => unknown;
}>();

const { nextIndex } = useZindex();
const zIndex = nextIndex();

const isOpen = ref(false);
const overflowBoundaryElement = ref(null);
const isTruncatingBadges = ref(true);
/**
 * We can't use the ref generic here because vue will change the type
 * of `value` to UnwrapRef<T> which will break the type of the `value`
 * property on the Selectable interface when we push to the array.
 * @see https://github.com/vuejs/composition-api/issues/432#issuecomment-655326830
 */
const overflowingSelections = ref([]) as Ref<Selectable<T>[]>;

/**
 * Separate overflowing selections for the modal summary section so
 * trigger and modal don’t compete when updating their own overflow arrays.
 */
const modalOverflowingSelections = ref([]) as Ref<Selectable<T>[]>;

const modalOverflowBoundaryElement = ref(null);

/**
 * Derive the selected values into a set to speed up checks on selected values.
 * This directly helps with "Select All" checks.
 */
const modelValueSet = computed(() =>
  props.modelValue.reduce((acc, curr) => {
    acc.add(curr.value);
    return acc;
  }, new Set<T>())
);
const areAllSelected = computed(() => props.options.every((o) => modelValueSet.value.has(o.value)));

/**
 * Selectables that are overflowing the bounds of the list
 * ordered to match the selection order.
 */
const orderedOverflowingSelections = computed<Selectable<T>[]>(() => {
  return (
    props.modelValue?.filter((selectedOption) => {
      return overflowingSelections.value.some((option) => option.value === selectedOption.value);
    }) || []
  );
});

const orderedModalOverflowingSelections = computed<Selectable<T>[]>(() => {
  return (
    props.modelValue?.filter((selectedOption) => {
      return modalOverflowingSelections.value.some(
        (option) => option.value === selectedOption.value
      );
    }) || []
  );
});
/**
 * When a Selectable is removed, ensure it is removed
 * from the overflowingSelections array.
 */
watch(
  () => props.modelValue,
  () => {
    overflowingSelections.value = overflowingSelections.value.filter(
      (option) => props.modelValue?.some((selectedOption) => selectedOption.value === option.value)
    );

    modalOverflowingSelections.value = modalOverflowingSelections.value.filter(
      (option) => props.modelValue?.some((selectedOption) => selectedOption.value === option.value)
    );
  }
);

const {
  filteredOptions,
  focusOnInput,
  focusOnTrigger,
  handleSearchQuery,
  hasNoResultsForQuery,
  searchInputElement,
  searchQuery,
  triggerClasses,
  triggerElement,
  triggerEvents
} = useSelectBox<T>(props);

function onBadgeIntersection(
  option: Selectable<T>,
  index: number,
  [{ isIntersecting }]: IntersectionObserverEntry[]
) {
  if (isSelected(option) && !isIntersecting) {
    overflowingSelections.value.push(option);
  } else {
    // if the current badge should be visible then all preceding indices should be visible as well
    const toRemove = props.modelValue
      .slice(0, index)
      .map((selectedOption) => selectedOption.value)
      .concat(option.value);
    overflowingSelections.value = overflowingSelections.value.filter(
      (selectedOption) => !toRemove.includes(selectedOption.value)
    );
  }
}

function onModalBadgeIntersection(
  option: Selectable<T>,
  index: number,
  [{ isIntersecting }]: IntersectionObserverEntry[]
) {
  if (isSelected(option) && !isIntersecting) {
    modalOverflowingSelections.value.push(option);
  } else {
    const toRemove = props.modelValue
      .slice(0, index)
      .map((selectedOption) => selectedOption.value)
      .concat(option.value);

    modalOverflowingSelections.value = modalOverflowingSelections.value.filter(
      (selectedOption) => !toRemove.includes(selectedOption.value)
    );
  }
}

/**
 * Handle the edge case where the overflow badge is only partially visible.
 * In this case, we should also add the prev option to overflow.
 */
function onFirstBadgeIntersection(
  option: Selectable<T>,
  index: number,
  [{ isIntersecting }]: IntersectionObserverEntry[]
) {
  if (isFirstInvisibleBadge(option) && !isIntersecting && index > 0) {
    const prevIndex = index - 1;
    overflowingSelections.value.push(props.modelValue[prevIndex]);
  }
}

function onFirstModalBadgeIntersection(
  option: Selectable<T>,
  index: number,
  [{ isIntersecting }]: IntersectionObserverEntry[]
) {
  if (isFirstInvisibleBadgeModal(option) && !isIntersecting && index > 0) {
    const prevIndex = index - 1;
    modalOverflowingSelections.value.push(props.modelValue[prevIndex]);
  }
}

function isBadgeOverflowing(option: Selectable<T>) {
  return (
    isTruncatingBadges.value &&
    orderedOverflowingSelections.value.some(
      (selectedOption) => selectedOption.value === option.value
    )
  );
}

function isBadgeOverflowingModal(option: Selectable<T>) {
  return (
    isTruncatingBadges.value &&
    orderedModalOverflowingSelections.value.some(
      (selectedOption) => selectedOption.value === option.value
    )
  );
}

function isFirstInvisibleBadge(option: Selectable<T>) {
  return orderedOverflowingSelections?.value[0]?.value === option.value;
}

function isFirstInvisibleBadgeModal(option: Selectable<T>) {
  return orderedModalOverflowingSelections?.value[0]?.value === option.value;
}

function isSelected(option: Selectable<T>) {
  return modelValueSet.value.has(option.value);
}

function deselect(option: Selectable<T>) {
  emit(
    'update:modelValue',
    props?.modelValue?.filter((selectedValue) => selectedValue.value !== option.value) || []
  );
}

function deselectFromBadge(option: Selectable<T>) {
  deselect(option);

  if (!isOpen.value) {
    return focusOnTrigger();
  }

  focusOnInput();
}

function handleModelValueUpdate(value) {
  if (!Array.isArray(value)) {
    emit('update:modelValue', [value]);
    return;
  }

  emit('update:modelValue', value);
}

function handleSelectAll() {
  if (areAllSelected.value) {
    handleModelValueUpdate([]);
  } else {
    const currentlyNotSelected = props.options.filter((o) => !modelValueSet.value.has(o.value));
    const all = (props.modelValue ?? []).concat(...currentlyNotSelected);
    handleModelValueUpdate(all);
  }
}

function closeCombobox() {
  isOpen.value = false;
}

function toggleTruncatingBadges() {
  isTruncatingBadges.value = !isTruncatingBadges.value;
}

function closeSelect() {
  isOpen.value = false;
}
</script>

<template>
  <ComboboxRoot
    multiple
    v-model:open="isOpen"
    :modelValue="modelValue"
    @update:modelValue="handleModelValueUpdate"
    @update:open="$emit('open')"
    :filterFunction="(opts) => opts"
    :disabled="isDisabled || isReadonly"
  >
    <div class="flex w-full items-start gap-x-2">
      <ComboboxAnchor class="w-full min-w-0">
        <ComboboxTrigger
          v-bind="$attrs"
          ref="triggerElement"
          tabindex="0"
          :class="[...triggerClasses, 'group', 'text-sm']"
          v-on="triggerEvents"
        >
          <ul
            class="-m-[3px] flex min-w-0 flex-1 items-center gap-1 overflow-x-clip rounded-lg"
            :class="{
              'flex-wrap': !isTruncatingBadges
            }"
            ref="overflowBoundaryElement"
          >
            <li v-if="(modelValue?.length ?? 0) === 0" class="pl-1.5 text-sm text-slate-400">
              {{ placeholder }}
            </li>
            <template v-if="selectAll && selectAllViewAs === 'single' && areAllSelected">
              <Badge
                variant="soft"
                shape="rounded"
                :size="BadgeSize.lg"
                :class="[
                  'whitespace-nowrap border-none font-semibold text-slate-700',
                  isDisabled && 'pointer-events-none'
                ]"
                tabindex="-1"
              >
                {{ selectAllLabel }}
              </Badge>
            </template>

            <template v-else v-for="(selectable, i) in modelValue" :key="selectable.value">
              <li class="relative">
                <Badge
                  v-if="isFirstInvisibleBadge(selectable) && isTruncatingBadges"
                  :label="`+${orderedOverflowingSelections.length}`"
                  variant="soft"
                  shape="rounded"
                  :size="BadgeSize.lg"
                  class="absolute border-none font-semibold text-slate-700"
                  tabindex="-1"
                  v-intersection-observer="[
                    (observerEntries: IntersectionObserverEntry[]) =>
                      onFirstBadgeIntersection(selectable, i, observerEntries),
                    { root: overflowBoundaryElement, threshold: 1 }
                  ]"
                />
                <Badge
                  variant="soft"
                  shape="rounded"
                  :size="BadgeSize.lg"
                  :class="[
                    'invisible whitespace-nowrap border-none font-semibold text-slate-700',
                    !isBadgeOverflowing(selectable) && '!visible',
                    isDisabled && 'pointer-events-none'
                  ]"
                  tabindex="-1"
                  :clearable="!isDisabled && !isReadonly"
                  @onClear="() => deselectFromBadge(selectable)"
                >
                  {{ selectable.label }}
                </Badge>
                <span
                  aria-hidden="true"
                  v-intersection-observer="[
                    (observerEntries: IntersectionObserverEntry[]) =>
                      onBadgeIntersection(selectable, i, observerEntries),
                    { root: overflowBoundaryElement }
                  ]"
                />
              </li>
            </template>
          </ul>
          <LockIcon
            v-if="isReadonly === true"
            class="h-4.5 w-4.5 shrink-0 text-slate-500 text-slate-500/25"
          />
          <AngleDownIcon v-else class="h-4.5 w-4.5 text-slate-500" />
        </ComboboxTrigger>
      </ComboboxAnchor>
      <div class="flex h-[42px] grow-0 items-center md:h-8">
        <IconButton
          v-if="!isTruncatingBadges"
          :icon="SortingIcon"
          ariaLabel="Show all selected options"
          :size="ButtonSize.md"
          variant="invisible"
          @click.stop="toggleTruncatingBadges"
        />
        <IconButton
          v-if="isTruncatingBadges"
          :icon="DirectionIcon"
          :isDisabled="!orderedOverflowingSelections.length"
          ariaLabel="Hide all selected options"
          :size="ButtonSize.md"
          variant="invisible"
          @click.stop="toggleTruncatingBadges"
        />
      </div>
    </div>

    <ComboboxPortal>
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black/70 data-[state=open]:animate-overlay-show"
        :style="{ zIndex }"
      />
      <ComboboxContent
        align="start"
        @escapeKeyDown="focusOnTrigger"
        @interactOutside="focusOnTrigger"
        class="fixed inset-x-0 bottom-0 overflow-hidden rounded-t-xl border border-slate-200 bg-white"
        :class="{
          'top-6': options.length > 6
        }"
        :style="{ zIndex }"
      >
        <div class="flex items-center gap-x-3 border-b border-slate-200 bg-white px-2.5 py-2">
          <IconButton
            :icon="ArrowLeftIcon"
            :size="ButtonSize.lg"
            :variant="ButtonVariant.invisible"
            ariaLabel="Close list"
            class="block"
            @click.stop="closeSelect"
          />
          <p
            class="grow text-center text-base font-semibold leading-5 text-slate-900"
            :class="{
              'pr-15': !selectAll
            }"
          >
            {{ label ?? 'Select an option' }}
          </p>
          <IconButton
            v-if="selectAll"
            :ariaLabel="selectAllLabel"
            :icon="CheckIcon"
            :size="ButtonSize.lg"
            :variant="ButtonVariant.invisible"
            class="block"
            @click.stop="handleSelectAll"
          />
        </div>

        <div
          v-if="modelValue?.length"
          class="flex w-full items-start justify-normal gap-x-4 border-b border-slate-200 py-2 pl-5 pr-2.5"
        >
          <ul
            class="flex min-w-0 flex-1 gap-1.5 overflow-x-clip rounded-lg py-2"
            :class="{
              'flex-wrap': !isTruncatingBadges
            }"
            ref="modalOverflowBoundaryElement"
          >
            <template v-if="selectAll && selectAllViewAs === 'single' && areAllSelected">
              <Badge
                variant="soft"
                shape="rounded"
                :size="BadgeSize.lg"
                :class="[
                  'whitespace-nowrap border-none font-semibold text-slate-700',
                  isDisabled && 'pointer-events-none'
                ]"
                tabindex="-1"
              >
                {{ selectAllLabel }}
              </Badge>
            </template>
            <template v-else v-for="(selectable, i) in modelValue" :key="selectable.value">
              <li class="relative">
                <Badge
                  v-if="isFirstInvisibleBadgeModal(selectable) && isTruncatingBadges"
                  :label="`+${orderedModalOverflowingSelections.length}`"
                  variant="soft"
                  shape="rounded"
                  :size="BadgeSize.lg"
                  class="absolute border-none font-semibold text-slate-700"
                  tabindex="-1"
                  v-intersection-observer="[
                    (observerEntries: IntersectionObserverEntry[]) =>
                      onFirstModalBadgeIntersection(selectable, i, observerEntries),
                    { root: modalOverflowBoundaryElement, threshold: 1 }
                  ]"
                />
                <Badge
                  variant="soft"
                  shape="rounded"
                  :size="BadgeSize.lg"
                  :class="[
                    'invisible whitespace-nowrap border-none font-semibold text-slate-700',
                    !isBadgeOverflowingModal(selectable) && '!visible',
                    isDisabled && 'pointer-events-none'
                  ]"
                  tabindex="-1"
                  :clearable="!isDisabled && !isReadonly"
                  @onClear="() => deselectFromBadge(selectable)"
                >
                  {{ selectable.label }}
                </Badge>
                <span
                  aria-hidden="true"
                  v-intersection-observer="[
                    (observerEntries: IntersectionObserverEntry[]) =>
                      onModalBadgeIntersection(selectable, i, observerEntries),
                    { root: modalOverflowBoundaryElement }
                  ]"
                />
              </li>
            </template>
          </ul>
          <LockIcon
            v-if="isReadonly === true"
            class="h-4.5 w-4.5 shrink-0 text-slate-500 text-slate-500/25"
          />
          <div class="flex h-[42px] grow-0 items-center md:h-8">
            <IconButton
              v-if="!isTruncatingBadges"
              :icon="SortingIcon"
              ariaLabel="Show all selected options"
              :size="ButtonSize.lg"
              variant="invisible"
              @click.stop="toggleTruncatingBadges"
            />
            <IconButton
              v-if="isTruncatingBadges"
              :icon="DirectionIcon"
              :isDisabled="!orderedOverflowingSelections.length"
              ariaLabel="Hide all selected options"
              :size="ButtonSize.lg"
              variant="invisible"
              @click.stop="toggleTruncatingBadges"
            />
          </div>
        </div>

        <ComboboxSearchInput
          v-if="searchable"
          ref="searchInputElement"
          :searchQuery="searchQuery"
          :isLoading="isLoading"
          isMobile
          @searchQuery="handleSearchQuery"
        />

        <SelectBoxViewport :as="ComboboxViewport" isMobile>
          <slot name="options">
            <SelectBoxItem
              v-if="selectAll && !hasNoResultsForQuery && options.length"
              :as="ComboboxItem"
              :checked="areAllSelected"
              isMobile
              value="SELECT_ALL"
              @click="handleSelectAll"
              @select.prevent
            >
              <template #default="{ checked }">
                <Checkbox :checked="checked" />
                <p>{{ selectAllLabel }}</p>
              </template>
            </SelectBoxItem>
            <SelectBoxItem
              :as="ComboboxItem"
              v-for="option in filteredOptions"
              :checked="isSelected(option)"
              :class="{
                'ml-4': option.header
              }"
              :disabled="option.disabled"
              :key="option.value?.toString() ?? option.label"
              :value="option"
              isMobile
              @click="focusOnInput"
            >
              <template #default="{ checked }">
                <slot name="option" :checked :option :close="closeCombobox">
                  <Checkbox :checked="checked" />
                  <div>
                    <p>{{ option.label }}</p>
                    <p v-if="option.description" class="text-2xs text-slate-400">
                      {{ option.description }}
                    </p>
                  </div>
                </slot>
              </template>
            </SelectBoxItem>
          </slot>
          <slot name="noResults">
            <MobileNoResults v-if="hasNoResultsForQuery" />
            <NoOptions v-else-if="!searchQuery && !options.length" />
          </slot>
        </SelectBoxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>
